<template>
    <div class="flex flex-col gap-8" >
        <div v-if="role == 'superadmin'"  class="flex gap-10 rounded-xl justify-center bg-[var(--c-primary)] p-4">
            <ButtonWhite @click="openDeleteConfirmModal">
                {{ $t('supplyGas.details.buttons.delete') }}
            </ButtonWhite>

            <RouterLink v-if="!isEstate"
                :to="{
                    path: `/add-gas/${id}`,
                    query: {
                        propertyName: supplysData?.property?.name,
                        ownerId: supplysData?.property?.owner_id?.toString(),
                        cups: supplysData?.cups,
                        rate: supplysData?.rate,
                        contractEnd: supplysData?.contract_expiration,
                        observations: supplysData?.observations,
                        address: supplysData?.address,
                        company: supplysData?.company,
                        cif: supplysData?.cif,
                        iban: supplysData?.iban
                    }
                }"
            >
                <ButtonWhite>{{ $t('supplyGas.details.buttons.modify') }}</ButtonWhite>
            </RouterLink>
        </div>

        <titlePrimary :title="isEstate ? $t('property.estateSupplies.detail.title.gas') : $t('supplyGas.details.title')" class="w-full" />

    <!-- Crear solicitud desde este CUPS (ADS-0125): el formulario recibe el suministro de origen. -->
    <div v-if="supplysData" class="flex justify-end">
      <NewRequestDropdown :supply="supplysData" :label="$t('supplyRequests.create')" />
    </div>

        <div v-if="loading" class="text-center p-4">
            {{ $t('supplyGas.details.loading') }}
        </div>
        <div v-else-if="error" class="text-red-500 text-center p-4">
            {{ error }}
        </div>

        <div v-else-if="supplysData" class="contents">
            <EstateSupplyDetailCard v-if="isEstate" :supply="supplysData" type="gas" />
            <CommunitySupplyDetailCard v-else :supply="supplysData" type="gas" />
        </div>

        <div v-if="showDeleteConfirmModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div class="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-lg">
                <h3 class="text-lg font-semibold text-[#385660] mb-2">Confirmar eliminación</h3>
                <p class="text-gray-600 mb-6">¿Seguro que quieres eliminar este suministro de gas? Esta acción no se puede deshacer.</p>
                <div class="flex justify-end gap-3">
                    <button
                        @click="closeDeleteConfirmModal"
                        :disabled="isDeleting"
                        class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        @click="deleteSupplys"
                        :disabled="isDeleting"
                        class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                        {{ isDeleting ? 'Eliminando...' : 'Eliminar' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>


<script setup >
import titlePrimary from '@/components/title/title-primary.vue';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import NewRequestDropdown from '@/components/button/NewRequestDropdown.vue';
import EstateSupplyDetailCard from '@/components/supply/EstateSupplyDetailCard.vue';
import CommunitySupplyDetailCard from '@/components/supply/CommunitySupplyDetailCard.vue';

import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const id = route.params.id;
const { t } = useI18n();

const role = ref(localStorage.getItem('role'));

// El detalle se decide por la finca del suministro, no por el rol de quien mira:
// un usuario vertical+horizontal ve cada CUPS según sea de inmueble o de comunidad.
const isEstate = computed(() => supplysData.value?.property?.type === 'inmueble');

const supplysData = ref(null);
const loading = ref(true);
const error = ref(null);

// Estados para el modal
const isDeleting = ref(false);
const showDeleteConfirmModal = ref(false);

const openDeleteConfirmModal = () => {
    showDeleteConfirmModal.value = true;
};

const closeDeleteConfirmModal = () => {
    if (isDeleting.value) return;
    showDeleteConfirmModal.value = false;
};

const fetchSupplyDetail = async () => {
    if (!id) {
        error.value = t('supplyGas.details.errors.noId');
        loading.value = false;
        return;
    }
    loading.value = true;
    error.value = null;
    try {
        const response = await axios.get(
            `${import.meta.env.VITE_API_URL}/supply/get/${id}`,
            {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            }
        );
        if (response.data?.value) {
            supplysData.value = response.data.value;
        } else {
            supplysData.value = null;
            error.value = 'No se encontraron datos.';
        }
    } catch (err) {
        console.error('Error fetching detail:', err);
        error.value = 'Failed to load details.';
        supplysData.value = null;
    } finally {
        loading.value = false;
    }
};

const deleteSupplys = async () => {
    if (!id) {
        error.value = 'No ID provided in route.';
        return;
    }

    isDeleting.value = true;
    try {
        const response = await axios.delete(
            `${import.meta.env.VITE_API_URL}/supply/delete/${id}`,
            {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            }
        );
        if (response.data?.value) {
            showDeleteConfirmModal.value = false;
            window.location.href = '/gas';
        } else {
            error.value = 'Error al eliminar el suministro.';
        }
    } catch (err) {
        console.error('Error deleting gas:', err);
        error.value = 'Error al eliminar el suministro.';
    } finally {
        isDeleting.value = false;
    }
};

onMounted(() => {
    fetchSupplyDetail();
});
</script>

<style scoped>
.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.contents {
    display: contents;
}
</style>
